import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate  } from "react-router-dom";

import { showErrorMessage, showSuccessMessage } from "helpers/notifications";
import { formatShortAddress } from "helpers/address";
import { TransportService } from "service/TransportService";
import { TRANSPORT } from "helpers/routes";

import ContentContainer from "components/common/InfoPopup/ContentContainer";
import { useChatSidebar } from "context/ChatSidebarContext";
import HeaderContainer from "components/common/InfoPopup/HeaderContainer";
import Navigation from "components/common/InfoPopup/Navigation";
import { ICON } from "components/common/IconButton";

import CompanyData from "components/common/InfoPopup/CompanyData";
import TransportDetails from "components/TransportComponents/TransportDetails";
import CompanyDocuments from "components/common/InfoPopup/CompanyDocuments";
import CompanyRoute from "components/common/InfoPopup/CompanyRoute";
import Contacts from "components/common/InfoPopup/Contacts";
import EmployeesSection from "components/common/InfoPopup/EmployeesSection";
import CompanyReviews from "components/common/InfoPopup/CompanyReviews";
import { ENTITY } from "constants/entity";
import { useMedia } from "hooks/useMedia";

import TooltipButton from "components/common/InfoPopup/TooltipButton";
import DeleteModal from "../../PopupForms/DeleteDialog/DeleteModal";
import styles from "./styles.module.scss";

const COMPANY_POPUP_MENU = {
    ABOUT: "about",
    ROUTE: "route",
    DETAIL: "detail",
    CONTACTS: "contacts",
    NOTES: "notes",
    REVIEW: "review",
    DOCUMENTS: "documents",
    EMPLOYEES: "employees",
};

export default function ExchangeInfo({ data, onClose, isOpen, updateData }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { openChat, openPopupChat } = useChatSidebar();
    const { isDesktop } = useMedia();

    const [currentSection, setCurrentSection] = useState(COMPANY_POPUP_MENU.DETAIL);

    const profileData = useSelector((state) => state.profile.profileData);
    const isMyExchange = data && profileData && profileData.id === data.user_id;
    const canRequestDocuments = data && data.use_chat && !isMyExchange && data.company_id !== profileData.company_id;

    const handleMenuClick = (id) => () => {
        setCurrentSection(id);
    };

    const handleDelete = (id, callback) => {
        TransportService.deleteExchange(id)
            .then(async () => {
                showSuccessMessage(t("successDelete"));
                await updateData();
                callback();
                onClose();
            })
            .catch((e) => {
                console.log(e);
                showErrorMessage(t("error"));
            });
    };

    const handleEditClick = () => {
        navigate(`/transport/edit?id=${data.id}`);
    };

    const handleUpToTopClick = () => {
        TransportService.exchangeUp(data.id)
            .then(() => {
                showSuccessMessage(t("success"));
                updateData();
            })
            .catch((e) => {
                console.log(e);
                showErrorMessage(t("error"));
            });
    };

    const handleToggleActive = () => {
        const wasActive = data.is_active;
        TransportService.toggleActivation(data.id)
            .then(() => {
                showSuccessMessage(t("success"));
                if (wasActive) {
                    onClose();
                }
                updateData();
            })
            .catch((e) => {
                console.log(e);
                showErrorMessage(t("error"));
            });
    };

    const navigationItems = [
        {
            id: COMPANY_POPUP_MENU.DETAIL,
            active: COMPANY_POPUP_MENU.DETAIL === currentSection,
            content: t("transport.modal.learnMore"),
        },
        {
            id: COMPANY_POPUP_MENU.ROUTE,
            active: COMPANY_POPUP_MENU.ROUTE === currentSection,
            content: t("transport.modal.route"),
        },
        {
            id: COMPANY_POPUP_MENU.CONTACTS,
            active: COMPANY_POPUP_MENU.CONTACTS === currentSection,
            content: t("transport.modal.contacts"),
        },
        {
            id: COMPANY_POPUP_MENU.ABOUT,
            active: COMPANY_POPUP_MENU.ABOUT === currentSection,
            content: t("transport.modal.mainInfo"),
        },
        {
            id: COMPANY_POPUP_MENU.EMPLOYEES,
            active: COMPANY_POPUP_MENU.EMPLOYEES === currentSection,
            content: t("transport.modal.employees"),
        },
    ];
    if (data && ((!isMyExchange) || (data.documents && data.documents.length > 0))) {
        navigationItems.push({
            id: COMPANY_POPUP_MENU.DOCUMENTS,
            active: COMPANY_POPUP_MENU.DOCUMENTS === currentSection,
            content: t("transport.modal.documents"),
        });
    }
    navigationItems.push({
        id: COMPANY_POPUP_MENU.REVIEW,
        active: COMPANY_POPUP_MENU.REVIEW === currentSection,
        content: t("exchangeModal.review_section.heading"),
    });

    const handleOpenChat = () => {
        // Владелец пишет клиенту (dealEmployee), клиент пишет владельцу (user)
        const employee = isMyExchange ? data.dealEmployee : data.user;
        const employeeId = employee?.id;
        // Передаём данные о транспорте для создания запроса на сделку
        const dealData = {
            item: {
                ...data,
                loading_from: data.available_from,
                unloading_to: data.available_to,
            },
            type: ENTITY.TRANSPORT,
            employee: {
                id: employee?.id,
                firstname: employee?.firstname,
                lastname: employee?.lastname,
                picture: employee?.picture,
                company: employee?.company || data.user?.company,
                organization_name: employee?.organization_name,
            },
        };

        if (isDesktop) {
            openChat(employeeId, dealData);
        } else {
            // На мобильных открываем попап-чат
            openPopupChat({
                employee: dealData.employee,
                item: dealData.item,
                type: dealData.type,
            });
        }
    };

    const componentToRender = {
        [COMPANY_POPUP_MENU.ABOUT]: <CompanyData companyData={data && data.user} />,
        [COMPANY_POPUP_MENU.DETAIL]: <TransportDetails transport={data} />,
        [COMPANY_POPUP_MENU.DOCUMENTS]: (
            <CompanyDocuments
                documentsData={data && data.documents}
                showExpectedDocuments={!isMyExchange}
                onRequestDocument={canRequestDocuments ? handleOpenChat : undefined}
            />
        ),
        [COMPANY_POPUP_MENU.ROUTE]: (
            <CompanyRoute
                loadingFrom={data && [{ lat: data.loading_address.location.latitude, ln: data.loading_address.location.longitude }]}
                loadingTo={data && [{ lat: data.unloading_address.location.latitude, ln: data.unloading_address.location.longitude }]}
                data={data}
            />
        ),
        [COMPANY_POPUP_MENU.CONTACTS]: (
            <Contacts contactId={data && data.id} apiRoute={TRANSPORT.getContacts} />
        ),
        [COMPANY_POPUP_MENU.EMPLOYEES]: (
            <EmployeesSection
                companyId={data && data.user && data.user.company_id}
            />
        ),
        [COMPANY_POPUP_MENU.REVIEW]: (
            <CompanyReviews companyId={data && data.user && data.user.company_id} />
        ),
    };
    useEffect(() => {
        if (isMyExchange && data && !(data.documents && data.documents.length > 0) && currentSection === COMPANY_POPUP_MENU.DOCUMENTS) {
            setCurrentSection(COMPANY_POPUP_MENU.DETAIL);
        }
    }, [currentSection, data, isMyExchange]);
    const popupButtons = isMyExchange && (
      <div className={styles.buttons_container}>
          {!data.isExpired && !data.isActiveProposal && (
              <div className={styles.buttons_container__tooltip_container}>
                  <p>{t("upToTop")}</p>
                  <TooltipButton
                      onClick={handleUpToTopClick}
                      hintText={data.can_up_to_top ? t("upToTop") : t("upToTopDisabled")}
                      disabled={!data.can_up_to_top}
                      icon={ICON.toTop}
                  />
              </div>
          )}
          {!data.isActiveProposal && (
              <div className={styles.buttons_container__tooltip_container}>
                  <p>{t("edit")}</p>
                  <TooltipButton
                      onClick={handleEditClick}
                      hintText={t("edit")}
                      icon={ICON.edit}
                  />
              </div>
          )}
          {!data.isExpired && !data.isActiveProposal && (
              <div className={styles.buttons_container__tooltip_container}>
                  <p>{data.isActive ? t("removeFromStock") : t("addToStock")}</p>
                  <TooltipButton
                      onClick={handleToggleActive}
                      hintText={data.is_active ? t("removeFromStock") : t("addToStock")}
                      icon={data.is_active ? ICON.eyeInvisible : ICON.eye}
                  />
              </div>
          )}
          <div className={styles.buttons_container__tooltip_container}>
              <p>{t("transport.delete")}</p>
              <DeleteModal
                  onDeleteClick={handleDelete}
                  data={{
                      title: t("transport.delete"),
                      id: data.id,
                      name: t("transport.transport"),
                  }}
              />
          </div>
      </div>
  );

    // Кнопка чата: для создателя - только когда есть dealEmployee (сделка принята), для других - всегда
    const showChatButton = data && data.use_chat && (
      isMyExchange
        ? !!data.dealEmployee  // Создатель видит кнопку когда есть клиент (сделка принята)
        : data.company_id !== profileData.company_id  // Другие могут связаться
    );

    return (
        <ContentContainer
            onClosePopup={onClose}
            isOpen={isOpen}
            popupButtons={popupButtons}
        >
            {data && (
                <>
                    <HeaderContainer
                        companyName={data.user.company.name}
                        companyId={data.user.company.id}
                        isVerified={Boolean(data.user.company.is_verified)}
                        companyLogo={
                            data.company_logo_url_small && data.company_logo_url_small.data
                        }
                        ratings={data.user.company.ratings}
                        showChatButton={showChatButton}
                        onChatClick={handleOpenChat}
                        items={[
                            {
                                title: t("exchangeModal.uploading"),
                                value: formatShortAddress(data.loading_address),
                            },
                            {
                                title: t("exchangeModal.unloading"),
                                value: formatShortAddress(data.unloading_address),
                            },
                            {
                                title: t("transportFields.main_section.name"),
                                value: data.own_name && data.own_name,
                            },
                        ]}
                    />
                    {isMyExchange && <div className={styles.mobileButtons}>{popupButtons}</div>}
                    <Navigation onClick={handleMenuClick} items={navigationItems} />
                    {componentToRender[currentSection]}
                </>
            )}
        </ContentContainer>
    );
}
